/**
 * Referrer-facing workflow stage model (Master Enhancement Plan §2.2 / §4.1).
 *
 * ONE stage vocabulary everywhere: the canonical 8 workflow stages from
 * `src/lib/workflow/transitions.ts` (Draft → Submitted for Review → Ready for
 * Screening → Ready for Processing → Billings → Immigration → Pre-Departure →
 * Closure). Referrer surfaces must never invent a second vocabulary — the old
 * referrer-only labels ("Application Started", "Qualified", "Partner
 * Assigned", …) are retired; legacy values found on stored records are
 * mapped onto the canonical stages here, in exactly one place.
 *
 * The stage shown is sourced from the workflow record: the application's
 * stored `stage` field is the workflow source of truth (see
 * `computeWorkflowState` in `src/lib/workflow/compute.ts` — "the stored stage
 * is the source of truth for where the applicant is"). When no usable stored
 * stage exists we fall back to the record's status fields, mirroring the
 * server's eager cascade at a coarser grain.
 */
import {
  STAGE_ORDER,
  STAGE_LABELS,
  normaliseStage,
  type CanonicalStage,
} from './workflow/transitions';

export { STAGE_ORDER, STAGE_LABELS };
export type { CanonicalStage };

/** The 8 workflow stages in lifecycle order, with display labels. */
export const WORKFLOW_STAGES: { key: CanonicalStage; label: string }[] =
  STAGE_ORDER.map((key) => ({ key, label: STAGE_LABELS[key] }));

/**
 * Legacy stage spellings that predate the canonical vocabulary (the retired
 * referrer CANDIDATE_STAGES list and the legacy program-pipeline ids), mapped
 * onto the canonical stage they belong to. Display-only mapping — records are
 * never rewritten from the client.
 */
const LEGACY_STAGE_MAP: Record<string, CanonicalStage> = {
  // Retired referrer vocabulary
  'application started': 'draft',
  'documents uploaded': 'submitted',
  'admin reviewed': 'readyForScreening',
  qualified: 'readyForScreening',
  matched: 'readyForProcessing',
  'partner assigned': 'readyForProcessing',
  'shared with employer': 'readyForProcessing',
  'interview scheduled': 'readyForProcessing',
  'offer sent': 'readyForProcessing',
  'placement confirmed': 'readyForProcessing',
  'financially cleared': 'immigration',
  'visa status': 'immigration',
  'visa approved': 'preDeparture',
  'flight booked': 'preDeparture',
  departed: 'preDeparture',
  arrived: 'closed',
  'program started': 'closed',
  rejected: 'closed',
  withdrawn: 'closed',
  // Legacy program-pipeline stage ids (DEFAULT_PIPELINE)
  screening: 'submitted',
  qualification: 'readyForScreening',
  match: 'readyForProcessing',
  placement: 'readyForProcessing',
  flight: 'preDeparture',
  visa: 'preDeparture',
  started: 'closed',
  alumni: 'closed',
};

const CANONICAL_SET = new Set<string>(STAGE_ORDER);

/**
 * Resolve the canonical workflow stage for a candidate/application record
 * (also works for commission records, which carry the application's stage
 * fields). Always returns one of the 8 canonical stages.
 */
export function resolveCandidateStage(record: any): CanonicalStage {
  if (!record) return 'draft';

  // A closure outcome pins the record to Closure regardless of stored stage.
  if (record.closureType) return 'closed';

  // 1. The stored workflow stage is the source of truth.
  const rawStage =
    record.stage || record.currentStage || record.workflowStage || record.canonicalStage || '';
  const normalised = normaliseStage(rawStage);
  if (CANONICAL_SET.has(normalised)) return normalised as CanonicalStage;

  // 2. Retired vocabularies map onto the canonical stages.
  const legacy = LEGACY_STAGE_MAP[String(rawStage || '').trim().toLowerCase()];
  if (legacy) return legacy;

  // 3. Status-based fallback (mirrors the server's eager cascade, coarsely):
  //    an unsubmitted application is a Draft; a submitted one that has not
  //    been approved sits in Submitted for Review; an approved one has at
  //    least reached Ready for Screening.
  const status = String(record.status || '').toLowerCase();
  const submission = String(record.submissionStatus || '').toLowerCase();
  if (status === 'returned' || submission === 'returned') return 'draft';
  if (status === 'draft' || submission === 'draft') return 'draft';
  if (
    record.isApprovedByAdmin === true ||
    record.preApprovalDecision === 'approved' ||
    status === 'approved' ||
    status === 'reviewed'
  ) {
    return 'readyForScreening';
  }
  if (submission === 'submitted' || status === 'submitted' || status === 'pending') {
    return 'submitted';
  }
  return 'draft';
}

/** Canonical display label for a record's workflow stage. */
export function candidateStageLabel(record: any): string {
  return STAGE_LABELS[resolveCandidateStage(record)];
}

/** Canonical label for a raw stage string (badge rendering from a string). */
export function stageLabelFromRaw(raw: unknown): string {
  const normalised = normaliseStage(raw);
  if (CANONICAL_SET.has(normalised)) return STAGE_LABELS[normalised as CanonicalStage];
  const legacy = LEGACY_STAGE_MAP[String(raw || '').trim().toLowerCase()];
  return legacy ? STAGE_LABELS[legacy] : STAGE_LABELS.draft;
}

/** Badge colour classes per canonical stage. */
export const STAGE_BADGE_CLASSES: Record<CanonicalStage, string> = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-amber-50 text-amber-700',
  readyForScreening: 'bg-indigo-50 text-indigo-700',
  readyForProcessing: 'bg-purple-50 text-purple-700',
  billings: 'bg-cyan-50 text-cyan-700',
  immigration: 'bg-blue-50 text-blue-700',
  preDeparture: 'bg-teal-50 text-teal-700',
  closed: 'bg-emerald-50 text-emerald-700',
};

/**
 * Plain-language, applicant-side synopsis per stage (plan §2.2 + Adian's
 * directive 2026-10-13): what the applicant has done to get here and what
 * they need to do next. Deliberately free of admin gates, overrides, and
 * internal mechanics — this is the referrer's view of the applicant's
 * journey, nothing more.
 */
export const STAGE_SYNOPSIS: Record<
  CanonicalStage,
  { applicantDone: string; applicantNext: string }
> = {
  draft: {
    applicantDone: 'Application created — it has not been submitted yet.',
    applicantNext: 'The applicant needs to complete and submit their application, including their required documents.',
  },
  submitted: {
    applicantDone: 'Application submitted to ISATS.',
    applicantNext: 'With ISATS for review. The applicant should upload any outstanding required documents and watch their inbox for messages.',
  },
  readyForScreening: {
    applicantDone: 'Application reviewed and accepted for screening.',
    applicantNext: 'The applicant needs to attend their screening interview when it is scheduled.',
  },
  readyForProcessing: {
    applicantDone: 'Screening passed.',
    applicantNext: 'The applicant needs to review and confirm their placement / admission details when they are shared.',
  },
  billings: {
    applicantDone: 'Placement confirmed.',
    applicantNext: 'The applicant needs to pay their outstanding program invoice(s).',
  },
  immigration: {
    applicantDone: 'Program fees cleared.',
    applicantNext: 'The applicant needs to complete their visa steps (application and embassy appointment) and share the outcome.',
  },
  preDeparture: {
    applicantDone: 'Visa outcome received.',
    applicantNext: 'The applicant needs to submit their flight and arrival details.',
  },
  closed: {
    applicantDone: 'Journey complete.',
    applicantNext: 'Nothing further is needed from the applicant.',
  },
};

/**
 * §4.1 — Partition a set of application records across the canonical 8
 * stages, in canonical order. Every record lands in exactly ONE bucket
 * (via resolveCandidateStage), so the counts always sum to the record
 * total — unlike the retired milestone buckets ('Application Started',
 * 'Shared with Employer', 'Visa Approved', …) whose overlapping counts
 * summed to well over 100% in the admin reports pipeline view. Shared by
 * every "by stage" breakdown / export so the vocabulary stays single-
 * sourced.
 */
export function stageBreakdown(
  records: readonly any[],
): { stage: CanonicalStage; label: string; count: number }[] {
  const counts = new Map<CanonicalStage, number>();
  for (const record of records || []) {
    const stage = resolveCandidateStage(record);
    counts.set(stage, (counts.get(stage) || 0) + 1);
  }
  return WORKFLOW_STAGES.map(({ key, label }) => ({
    stage: key,
    label,
    count: counts.get(key) || 0,
  }));
}

/**
 * PR-12 — Reports self-consistency. Any figure that purports to measure a
 * pipeline STAGE must be read off the stageBreakdown() partition — never
 * re-counted from a second field (the legacy `submissionStatus` flag is
 * not advanced by the workflow engine, which is how the admin Reports
 * page showed KPI "Submitted 6" next to stage "Submitted for Review 12").
 * These helpers only ever READ the partition rows, so a stage-derived KPI
 * or funnel step cannot disagree with the by-stage breakdown by
 * construction.
 */

/** Count for one canonical stage, read off stageBreakdown() rows. */
export function stageCount(
  rows: readonly { stage: CanonicalStage; count: number }[],
  stage: CanonicalStage,
): number {
  return rows.find((r) => r.stage === stage)?.count ?? 0;
}

/**
 * Cumulative funnel over the canonical stages: for each stage, how many
 * records sit in that stage OR any later stage (i.e. have reached it).
 * Derived purely from stageBreakdown() rows, so the first step equals the
 * record total and the last step equals the Closure bucket.
 */
export function cumulativeStageFunnel(
  rows: readonly { stage: CanonicalStage; label: string; count: number }[],
): { stage: CanonicalStage; label: string; count: number }[] {
  let reached = 0;
  return [...rows]
    .reverse()
    .map((r) => {
      reached += r.count;
      return { stage: r.stage, label: r.label, count: reached };
    })
    .reverse();
}

/** Days a candidate has sat untouched (for the "Needs attention" list). */
export function daysSinceUpdate(record: any, now: Date = new Date()): number | null {
  const raw = record?.updatedAt || record?.createdAt;
  if (!raw) return null;
  const t = new Date(raw).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}
